import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';

import type { LatestSiteData } from '../../interfaces/latest-site-data.interface';
import {
  formatAlertDate,
  isAlertDateValid,
  isWinterSeason,
  isWithinMinutes,
} from '../../utils/data-freshness.util';

type AlertType =
  | 'wbgt-danger'
  | 'wbgt-high'
  | 'temperature-today'
  | 'wind-actual'
  | 'wind-alert'
  | 'noise'
  | 'vibration'
  | 'ph-min'
  | 'ph-max';

interface AlertBannerItem {
  type: AlertType;
  message: string;
  styleClass: 'status-signal-20' | 'status-signal-11';
  safetyDocUrl?: string;
  secondaryMessage?: string;
}

const WBGT_SAFETY_DOC_URL = '/assets/pdf/wbgt.pdf';
const WIND_SAFETY_DOC_URL = '/assets/pdf/wind.pdf';

function isSameLocalDate(dateStr: string | null | undefined, referenceDate: Date): boolean {
  if (!dateStr) return false;

  const targetDate = new Date(dateStr);
  if (Number.isNaN(targetDate.getTime())) return false;

  return targetDate.getFullYear() === referenceDate.getFullYear()
    && targetDate.getMonth() === referenceDate.getMonth()
    && targetDate.getDate() === referenceDate.getDate();
}

@Component({
  selector: 'app-site-alert-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCardModule],
  templateUrl: './site-alert-banner.html',
  styleUrl: './site-alert-banner.scss',
})
export class SiteAlertBannerComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentDate = signal(new Date());
  private readonly effectiveReferenceDate = computed(() => this.referenceDate() ?? this.currentDate());

  public readonly latestSiteData = input<LatestSiteData | undefined>(undefined);
  public readonly isOuterUser = input<boolean>(false);
  public readonly referenceDate = input<Date | undefined>(undefined);

  public readonly alerts = computed<AlertBannerItem[]>(() => {
    const data = this.latestSiteData();
    if (!data) return [];

    const referenceDate = this.effectiveReferenceDate();
    const shouldShowSafetyDoc = !this.isOuterUser();
    const alerts: AlertBannerItem[] = [];
    const wbgtDangerDate = data.firstAlertDate;
    const wbgtHighDate = data.firstHighAlertDate;

    if (wbgtDangerDate && isSameLocalDate(wbgtDangerDate, referenceDate)) {
      alerts.push({
        type: 'wbgt-danger',
        message: `${formatAlertDate(wbgtDangerDate)} にWBGTステータスが 危険 になりました。`,
        styleClass: 'status-signal-20',
        safetyDocUrl: shouldShowSafetyDoc ? WBGT_SAFETY_DOC_URL : undefined,
      });
    } else if (wbgtHighDate && isSameLocalDate(wbgtHighDate, referenceDate)) {
      alerts.push({
        type: 'wbgt-high',
        message: `${formatAlertDate(wbgtHighDate)} にWBGTステータスが 厳重警戒 になりました。`,
        styleClass: 'status-signal-11',
        safetyDocUrl: shouldShowSafetyDoc ? WBGT_SAFETY_DOC_URL : undefined,
      });
    }

    if (
      isWinterSeason(referenceDate) &&
      isAlertDateValid(data.todayTemperatureAlertDate, 3, referenceDate)
    ) {
      alerts.push({
        type: 'temperature-today',
        message: '現在の温度が 5℃以下 です',
        styleClass: 'status-signal-20',
      });
    }

    if (isWithinMinutes(data.actualWindAttentionStatusDate, 10, referenceDate)) {
      alerts.push({
        type: 'wind-actual',
        message: `${formatAlertDate(data.actualWindAttentionStatusDate ?? '')} に風速ステータスが 警報 になりました。`,
        styleClass: 'status-signal-20',
        secondaryMessage: '揚重作業ストップ',
        safetyDocUrl: shouldShowSafetyDoc ? WIND_SAFETY_DOC_URL : undefined,
      });
    } else if (isAlertDateValid(data.firstWindAlertDate, 3, referenceDate)) {
      alerts.push({
        type: 'wind-alert',
        message: `${formatAlertDate(data.firstWindAlertDate ?? '')} に風速ステータスが 警報 になりました。`,
        styleClass: 'status-signal-20',
        secondaryMessage: '揚重作業ストップ',
        safetyDocUrl: shouldShowSafetyDoc ? WIND_SAFETY_DOC_URL : undefined,
      });
    }

    if (isAlertDateValid(data.noiseAlertDate, 3, referenceDate)) {
      alerts.push({
        type: 'noise',
        message: `${formatAlertDate(data.noiseAlertDate ?? '')} に騒音ステータスが 警報 になりました。`,
        styleClass: 'status-signal-20',
      });
    }

    if (isAlertDateValid(data.vibrationAlertDate, 3, referenceDate)) {
      alerts.push({
        type: 'vibration',
        message: `${formatAlertDate(data.vibrationAlertDate ?? '')} に振動ステータスが 警報 になりました。`,
        styleClass: 'status-signal-20',
      });
    }

    if (isAlertDateValid(data.minPhAlertDate, 3, referenceDate)) {
      alerts.push({
        type: 'ph-min',
        message: `${formatAlertDate(data.minPhAlertDate ?? '')} にpHステータスが 酸性 強 になりました。`,
        styleClass: 'status-signal-20',
      });
    }

    if (isAlertDateValid(data.maxPhAlertDate, 3, referenceDate)) {
      alerts.push({
        type: 'ph-max',
        message: `${formatAlertDate(data.maxPhAlertDate ?? '')} にpHステータスが アルカリ 強 になりました。`,
        styleClass: 'status-signal-20',
      });
    }

    return alerts;
  });

  constructor() {
    const timerId = setInterval(() => {
      this.currentDate.set(new Date());
    }, 60 * 1000);

    this.destroyRef.onDestroy(() => clearInterval(timerId));
  }
}
